import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, OrganizationContact, OrganizationRow } from '../api';
import { organizationEndpoints } from '../api';
import { groupSalesEndpoints } from '../api-group-sales';
import { groupBookingEndpoints } from '../api-group-bookings';
import { usePermissions } from '../session';
import { NotPermitted } from '../App';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * The organizations BRE sells to, as records (D-244).
 *
 * `organizations` has been the home of a house account and a sponsor since
 * 0013. Group sales never joined it: a group hold carried the name as free
 * text, so the school that books forty seats every December was a new string
 * each year and nobody could answer what they took last time, who the teacher
 * is, or whether they still owe us.
 *
 * The page is a list and a record, not a CRM. What earns its place is the
 * **history** on the right: every group hold this organization has taken, with
 * what it came to. That is the question a person actually has when the phone
 * rings.
 */

export default function OrganizationsPage() {
  const can = usePermissions();
  // Linked to from a group hold (D-336): ?id= opens that organization.
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(params.get('id'));
  const [adding, setAdding] = useState(false);
  const list = useQuery({ queryKey: ['organizations'], queryFn: organizationEndpoints.list });
  const types = useQuery({ queryKey: ['organizations', 'types'], queryFn: organizationEndpoints.types });

  if (!can('organizations.view')) return <NotPermitted permission="organizations.view" />;
  if (list.error) return <ErrorNote>{(list.error as ApiError).message}</ErrorNote>;

  const organizations = list.data?.organizations ?? [];
  const typeName = new Map((types.data?.types ?? []).map((t) => [t.code, t.name]));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Organizations</h1>
          <p className="mt-1 text-sm text-ink-600">
            Schools, businesses, nonprofits and the groups that book together. Kept once, so the group that comes back every year is the same record rather than a name typed
            again.
          </p>
        </div>
        {can('organizations.manage') ? <Button onClick={() => { setAdding(true); setSelected(null); }}>Add an organization</Button> : null}
      </div>

      {adding ? <OrganizationForm types={types.data?.types ?? []} onDone={(id) => { setAdding(false); setSelected(id); }} onCancel={() => setAdding(false)} /> : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Panel title={`${organizations.length} organization${organizations.length === 1 ? '' : 's'}`}>
          <Table headers={['Name', 'Kind', 'Contact']}>
            {organizations.length === 0 ? (
              <EmptyRow columns={3}>None yet. Add one when a group books, and it is there the next time they call.</EmptyRow>
            ) : (
              organizations.map((organization) => (
                <tr
                  key={organization.id}
                  className={`cursor-pointer border-b border-line last:border-0 ${selected === organization.id ? 'bg-page' : ''}`}
                  onClick={() => { setSelected(organization.id); setAdding(false); }}
                >
                  <td className="px-5 py-2">
                    <span className="font-medium text-ink-900">{organization.name}</span>
                    {organization.active ? null : <span className="ml-2 text-xs text-ink-400">inactive</span>}
                    {organization.city ? <div className="text-xs text-ink-400">{[organization.city, organization.region].filter(Boolean).join(', ')}</div> : null}
                  </td>
                  <td className="px-5 py-2">
                    <div className="flex flex-wrap gap-1">
                      {organization.types.length === 0 ? (
                        <span className="text-xs text-ink-400">—</span>
                      ) : (
                        organization.types.map((code) => (
                          <Badge key={code} tone="neutral">
                            {typeName.get(code) ?? code}
                          </Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-2 text-sm text-ink-600">
                    {organization.primary_email ?? '—'}
                    {organization.phone ? <div className="text-xs text-ink-400">{organization.phone}</div> : null}
                  </td>
                </tr>
              ))
            )}
          </Table>
        </Panel>

        {selected ? <OrganizationDetailPanel id={selected} types={types.data?.types ?? []} /> : null}
      </div>
    </div>
  );
}

function OrganizationDetailPanel({ id, types }: { id: string; types: { code: string; name: string }[] }) {
  const can = usePermissions();
  const [editing, setEditing] = useState(false);
  const detail = useQuery({ queryKey: ['organizations', id], queryFn: () => organizationEndpoints.get(id) });
  if (detail.error) return <ErrorNote>{(detail.error as ApiError).message}</ErrorNote>;
  if (!detail.data) return null;
  const { organization, group_holds: holds, sponsorships, contacts } = detail.data;

  if (editing) return <OrganizationForm types={types} existing={organization} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />;

  return (
    <div className="space-y-4">
      <Panel
        title={organization.name}
        actions={can('organizations.manage') ? <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button> : null}
      >
        <dl className="space-y-2 p-5 text-sm">
          {organization.legal_name ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">Legal name</dt>
              <dd>{organization.legal_name}</dd>
            </div>
          ) : null}
          <div className="flex gap-3">
            <dt className="w-32 shrink-0 text-ink-500">Contact</dt>
            <dd>
              {organization.primary_email ?? '—'}
              {organization.phone ? ` · ${organization.phone}` : ''}
            </dd>
          </div>
          {organization.address_line1 ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">Address</dt>
              <dd>{[organization.address_line1, organization.city, organization.region, organization.postal_code].filter(Boolean).join(', ')}</dd>
            </div>
          ) : null}
          {organization.house_account_enabled ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">House account</dt>
              <dd>
                <Badge tone="ok">enabled</Badge>
                {organization.house_account_limit_cents ? <span className="ml-2">up to {dollars(organization.house_account_limit_cents)}</span> : null}
              </dd>
            </div>
          ) : null}
          {detail.data.qbo_customer ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">QuickBooks</dt>
              {/* Found or made the first time one of its group holds was posted (D-352). */}
              <dd>
                {detail.data.qbo_customer.name ?? 'Customer'} <span className="text-xs text-ink-500">(customer {detail.data.qbo_customer.id})</span>
              </dd>
            </div>
          ) : null}
          {sponsorships.length > 0 ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">Sponsors as</dt>
              <dd>{sponsorships.map((s) => s.name).join(', ')}</dd>
            </div>
          ) : null}
          {organization.notes ? (
            <div className="flex gap-3">
              <dt className="w-32 shrink-0 text-ink-500">Notes</dt>
              {/* What the last person learned: they pay by check and it takes
                  three weeks, the contact changes every September. */}
              <dd className="whitespace-pre-wrap">{organization.notes}</dd>
            </div>
          ) : null}
        </dl>
      </Panel>

      <ContactsPanel organizationId={id} contacts={contacts} onChanged={() => void detail.refetch()} />

      <GroupOutingsPanel detail={detail.data} />

      <Panel title="What they have booked" description="Every group hold against this organization, newest first.">
        <Table headers={['Show', 'Seats', 'Status', 'Total']}>
          {holds.length === 0 ? (
            <EmptyRow columns={4}>Nothing yet. A group hold points here when it is made against this organization.</EmptyRow>
          ) : (
            holds.map((hold) => (
              <tr key={hold.id} className="border-b border-line last:border-0">
                <td className="px-5 py-2">
                  <Link to={`/group-holds/${hold.id}`} className="font-medium text-live hover:underline">
                    {hold.event_title}
                  </Link>
                  <div className="text-xs text-ink-400">
                    {formatWhen(hold.starts_at)} · {hold.order_number}
                    {hold.status === 'HELD' || hold.status === 'PAID' ? (
                      <>
                        {' · '}
                        <a href={groupSalesEndpoints.invoiceUrl(hold.id)} target="_blank" rel="noopener" className="text-live hover:underline">
                          Invoice (PDF)
                        </a>
                      </>
                    ) : null}
                  </div>
                </td>
                <td className="tabular px-5 py-2">{hold.seat_count}</td>
                <td className="px-5 py-2">
                  <Badge tone={hold.status === 'PAID' ? 'ok' : hold.status === 'RELEASED' ? 'bad' : 'warn'}>{hold.status.toLowerCase()}</Badge>
                </td>
                <td className="tabular px-5 py-2">{dollars(hold.cached_total_due_cents)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

/**
 * Its group outings (D-376): past and to come, the leads behind them, and what
 * its group orders have come to — with a shortcut to book last year's outing
 * again.
 */
function GroupOutingsPanel({ detail }: { detail: import('../api').OrganizationDetail }) {
  const can = usePermissions();
  const navigate = useNavigate();
  const bookings = detail.group_bookings ?? [];
  const leads = detail.group_leads ?? [];
  const spend = detail.group_spend;
  const last = leads.find((lead) => lead.status === 'COMPLETED' || lead.status === 'BOOKED');
  const rebook = useMutation({ mutationFn: (leadId: string) => groupBookingEndpoints.rebook(leadId), onSuccess: (r) => navigate(`/group-sales/leads/${r.lead.id}`) });
  if (bookings.length === 0 && leads.length === 0) return null;
  const now = new Date().toISOString();
  return (
    <Panel
      title="Group outings"
      description={spend ? `${spend.orders} group order${spend.orders === 1 ? '' : 's'}: ${dollars(spend.total_cents)}, ${dollars(spend.paid_cents)} paid.` : 'Its group outings.'}
      actions={last && can('group_sales.manage') ? <Button onClick={() => rebook.mutate(last.id)} disabled={rebook.isPending}>Rebook their last outing</Button> : null}
    >
      <Table headers={['Outing', 'Game', 'Status', 'Order']}>
        {bookings.map((b) => (
          <tr key={b.id} className="border-b border-line last:border-0">
            <td className="px-5 py-2">
              {b.lead_id ? <Link to={`/group-sales/leads/${b.lead_id}`} className="font-medium text-live hover:underline">{b.product_name}</Link> : b.product_name}
              {b.option_names ? <div className="text-xs text-ink-400">+ {b.option_names}</div> : null}
            </td>
            <td className="px-5 py-2">
              {b.event_title}
              <div className="text-xs text-ink-400">{formatWhen(b.starts_at)}{b.starts_at > now ? ' · to come' : ''}</div>
            </td>
            <td className="px-5 py-2"><Badge tone={b.status === 'BOOKED' ? 'ok' : b.status === 'HELD' ? 'warn' : 'neutral'}>{b.status.toLowerCase()}</Badge></td>
            <td className="px-5 py-2">{b.order_id ? <Link to={`/orders/${b.order_id}`} className="font-mono text-live hover:underline">{b.order_number}</Link> : '—'}{b.total_cents !== null ? <div className="text-xs text-ink-400">{dollars(b.paid_cents ?? 0)} of {dollars(b.total_cents)}</div> : null}</td>
          </tr>
        ))}
      </Table>
      {leads.length > 0 ? (
        <ul className="border-t border-line px-5 py-3 text-sm">
          {leads.map((lead) => (
            <li key={lead.id}>
              <Link to={`/group-sales/leads/${lead.id}`} className="text-live hover:underline">{lead.reference}</Link> · {lead.contact_name} · {lead.status_name}
              {lead.interest_name ? ` · ${lead.interest_name}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}

/**
 * The people at an organization (D-254).
 *
 * Stored in `renter_contacts`, which has held exactly this shape since 0013 and
 * was never written because renters were cut. They are `CONTACT` rather than
 * `INVITED`: nobody is being invited to a portal, and recording an invitation
 * that was never sent would be recording something untrue.
 */
function ContactsPanel({ organizationId, contacts, onChanged }: { organizationId: string; contacts: OrganizationContact[]; onChanged: () => void }) {
  const can = usePermissions();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', title: '' });
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      organizationEndpoints.addContact(organizationId, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        title: form.title.trim() || null,
      }),
    onSuccess: () => {
      setError(null);
      setAdding(false);
      setForm({ name: '', email: '', phone: '', title: '' });
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const remove = useMutation({
    mutationFn: (contactId: string) => organizationEndpoints.removeContact(organizationId, contactId),
    onSuccess: onChanged,
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <Panel
      title="People"
      description="Who to call. A contact does not need an account; one is only needed if they are ever to sign in."
      actions={can('organizations.manage') && !adding ? <Button variant="secondary" onClick={() => setAdding(true)}>Add</Button> : null}
    >
      <div className="p-5">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {adding ? (
          <div className="mb-3 space-y-2 border border-line p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={INPUT} />
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" className={INPUT} />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={INPUT} />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className={INPUT} />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => add.mutate()} disabled={add.isPending || !form.name.trim() || !form.email.trim()}>
                {add.isPending ? 'Saving…' : 'Add'}
              </Button>
              <button type="button" className="text-sm text-ink-500 underline" onClick={() => { setAdding(false); setError(null); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        {contacts.length === 0 ? (
          <p className="text-sm text-ink-500">Nobody yet.</p>
        ) : (
          <ul className="divide-y divide-line">
            {contacts.map((contact) => (
              <li key={contact.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span>
                  <span className="font-medium text-ink-900">{contact.name}</span>
                  {contact.title ? <span className="text-ink-500"> · {contact.title}</span> : null}
                  <span className="block text-xs text-ink-500">{[contact.email, contact.phone].filter(Boolean).join(' · ')}</span>
                </span>
                {can('organizations.manage') ? (
                  <button type="button" className="text-xs text-ink-400 underline" onClick={() => remove.mutate(contact.id)} disabled={remove.isPending}>
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

function OrganizationForm({
  types,
  existing,
  onDone,
  onCancel,
}: {
  types: { code: string; name: string }[];
  existing?: OrganizationRow;
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    legal_name: existing?.legal_name ?? '',
    primary_email: existing?.primary_email ?? '',
    phone: existing?.phone ?? '',
    address_line1: existing?.address_line1 ?? '',
    city: existing?.city ?? '',
    region: existing?.region ?? '',
    postal_code: existing?.postal_code ?? '',
    notes: existing?.notes ?? '',
    types: existing?.types ?? [],
  });

  const save = useMutation({
    mutationFn: () => {
      // Empty is null, not an empty string: "" in a column that means "we do
      // not know" reads as a value somebody entered.
      const payload = {
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || null,
        primary_email: form.primary_email.trim() || null,
        phone: form.phone.trim() || null,
        address_line1: form.address_line1.trim() || null,
        city: form.city.trim() || null,
        region: form.region.trim() || null,
        postal_code: form.postal_code.trim() || null,
        notes: form.notes.trim() || null,
        types: form.types,
      };
      return existing ? organizationEndpoints.update(existing.id, payload) : organizationEndpoints.create(payload);
    },
    onSuccess: (result) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onDone(result.organization.id);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const toggleType = (code: string) =>
    setForm((f) => ({ ...f, types: f.types.includes(code) ? f.types.filter((t) => t !== code) : [...f.types, code] }));

  return (
    <Panel title={existing ? `Edit ${existing.name}` : 'Add an organization'}>
      <div className="space-y-3 p-5">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        <Field label="Name" htmlFor="org-name">
          <input id="org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT} />
        </Field>
        <Field label="Legal name" htmlFor="org-legal" hint="Only if it differs from what everybody calls them; it is what goes on an invoice.">
          <input id="org-legal" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className={INPUT} />
        </Field>
        <fieldset>
          <legend className="text-sm font-medium text-ink-700">Kind</legend>
          {/* Several, because a school that also sponsors a show is both, and
              making somebody pick one would lose whichever they did not pick. */}
          <p className="text-xs text-ink-500">More than one is fine.</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {types.map((type) => (
              <label key={type.code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.types.includes(type.code)} onChange={() => toggleType(type.code)} className="h-4 w-4 accent-brand" />
                {type.name}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Email" htmlFor="org-email">
            <input id="org-email" type="email" value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} className={INPUT} />
          </Field>
          <Field label="Phone" htmlFor="org-phone">
            <input id="org-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={INPUT} />
          </Field>
        </div>
        <Field label="Address" htmlFor="org-address">
          <input id="org-address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className={INPUT} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="City" htmlFor="org-city">
            <input id="org-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={INPUT} />
          </Field>
          <Field label="State" htmlFor="org-region">
            <input id="org-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={INPUT} />
          </Field>
          <Field label="ZIP" htmlFor="org-zip">
            <input id="org-zip" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={INPUT} />
          </Field>
        </div>
        <Field label="Notes" htmlFor="org-notes" hint="What the next person needs to know: how they pay, who actually answers, what they always ask for.">
          <textarea id="org-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={INPUT} />
        </Field>
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending || form.name.trim().length === 0}>
            {save.isPending ? 'Saving…' : existing ? 'Save' : 'Add'}
          </Button>
          <button type="button" className="text-sm text-ink-500 underline" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Panel>
  );
}
